
import { useQuery } from '@tanstack/react-query';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { useAuth } from '@/hooks/use-auth';

export interface MemberRow {
  user_id: string;
  first_name: string;
  last_name: string;
  phone?: string | null;
  email?: string | null;
  birth_date?: string | null;
  street_address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  isAdmin: boolean;
  groups: string[];
}

export interface UseMembersParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useMembers({ page = 1, pageSize = 10, search = '' }: UseMembersParams) {
  const supabase = useSupabase();
  const { user, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['members', user?.id ?? null, page, pageSize, search],
    enabled: Boolean(isAuthenticated && user),
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // First, get all profiles with pagination from public view (bypasses RLS)
      let profilesQuery = supabase
        .from('profiles_public_v')
        .select('user_id, first_name, last_name, phone, email, birth_date, street_address, postal_code, city, created_at', { count: 'exact' })
        .range(from, to)
        .order('created_at', { ascending: false });

      const trimmed = search.trim();
      if (trimmed) {
        const normalized = trimmed
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        const clauses = [
          `first_name.ilike.%${trimmed}%`,
          `last_name.ilike.%${trimmed}%`,
          `email.ilike.%${trimmed}%`,
          `phone.ilike.%${trimmed}%`,
          `first_name_search.ilike.%${normalized}%`,
          `last_name_search.ilike.%${normalized}%`,
          `full_name_search.ilike.%${normalized}%`
        ];
        profilesQuery = profilesQuery.or(clauses.join(','));
      }

      const { data: profiles, error: profilesError, count } = await profilesQuery;
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      const userIds: string[] = (profiles || []).map((p: any) => p.user_id).filter(Boolean);
      const uuidPattern = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
      const uuidUserIds = userIds.filter((id) => uuidPattern.test(id));
      const clerkUserIds = userIds.filter((id) => !uuidPattern.test(id));

      // Get admin user_ids via public admin view
      const adminSet: Set<string> = new Set();
      if (uuidUserIds.length) {
        // Get all admin user_ids and filter in JavaScript to avoid URL length limits
        const { data: allAdminsData } = await supabase
          .from('user_admin_map_v')
          .select('user_id');
        const adminUserIds = new Set((allAdminsData || []).map((r: any) => r.user_id));
        uuidUserIds.forEach((userId) => {
          if (adminUserIds.has(userId)) {
            adminSet.add(userId);
          }
        });
      }
      if (clerkUserIds.length) {
        const { data: overrideAdmins } = await supabase
          .from('clerk_admin_overrides')
          .select('clerk_user_id')
          .in('clerk_user_id', clerkUserIds);
        (overrideAdmins || []).forEach((r: any) => adminSet.add(r.clerk_user_id));
      }

      // Get group memberships
      const groupsByUser = new Map<string, string[]>();
      if (uuidUserIds.length) {
        const { data: groupMemberships, error: groupError } = await supabase
          .from('group_members')
          .select('user_id, groups:groups!inner(name)')
          .in('user_id', uuidUserIds);

        if (groupError) {
          console.error('Error fetching group memberships:', groupError);
        } else {
          (groupMemberships || []).forEach((row: any) => {
            const name = row.groups?.name as string | undefined;
            if (!name) return;
            const list = groupsByUser.get(row.user_id) || [];
            list.push(name);
            groupsByUser.set(row.user_id, list);
          });
        }
      }

      const rows: MemberRow[] = (profiles || []).map((p: any) => ({
        user_id: p.user_id,
        first_name: p.first_name,
        last_name: p.last_name,
        phone: p.phone,
        email: p.email ?? null,
        birth_date: p.birth_date ?? null,
        street_address: p.street_address ?? null,
        postal_code: p.postal_code ?? null,
        city: p.city ?? null,
        isAdmin: adminSet.has(p.user_id),
        groups: groupsByUser.get(p.user_id) || [],
      }));

      // Get total admin count via admin map view
      const totalAdminIds = new Set<string>();
      {
        const { data: allAdmins } = await supabase
          .from('user_admin_map_v')
          .select('user_id');
        (allAdmins || []).forEach((r: any) => totalAdminIds.add(r.user_id));
      }
      {
        const { data: overrideAdmins } = await supabase
          .from('clerk_admin_overrides')
          .select('clerk_user_id');
        (overrideAdmins || []).forEach((r: any) => totalAdminIds.add(r.clerk_user_id));
      }
      const totalAdminCount = totalAdminIds.size;

      return { 
        rows, 
        total: count || 0, 
        adminCount: totalAdminCount || 0 
      };
    },
  });
}
